// ABOUTME: Lightweight builder preview of a dashboard cell's linked report (name + format/variant badge).
// ABOUTME: No report data is rendered while building — the dashboard viewer renders the live report. Click to pick/change/clear.
import { Box, ButtonBase, useTheme } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import ShRowSkeleton from "../../../../theme/skeleton/ShRowSkeleton";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";
import { dashboardCellKinds } from "../dashboardEnums";
import { dashboardElements } from "../dashboardElements";
import { RhDashboardChart } from "../../components/RhDashboardChart";

const VARIANT_LABEL = { top: "db_view_top", bottom: "db_view_bottom", movers: "db_view_movers", full: "db_view_full" };

export const RhDashboardCell = ({ chart, onPick, onClear, handleRemoveColumn, containerKey, si, ri, ci, di }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";
    const accent = isDark ? colors.blue : colors.primary;
    const stop = (e) => e.stopPropagation();

    const c = chart || {};
    const hasReport = c.reportId != null;
    const isComponent = c.kind === dashboardCellKinds.COMPONENT;
    const isPivot = c.kind === dashboardCellKinds.PIVOT;
    // A cell is filled by either a saved report OR a pre-built component (which has a componentKey, no reportId).
    const hasContent = hasReport || isComponent;
    const element = isComponent ? dashboardElements[c.componentKey] : null;

    if (!hasContent) {
        return (
            <Box
                sx={{
                    display:"flex",
                    flexDirection:"column",
                    gap:2,
                    width:1,
                    pb:6,
                }}
            >
                <ButtonBase
                    onPointerDown={stop}
                    onClick={(e) => { stop(e); onPick?.(); }}
                    sx={{
                        flex: 1,
                        borderRadius: 4,
                        gap: 1,
                        color: colors.txt["secondary"],
                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                        transition:"250ms",
                        "&:hover": { 
                            color: colors.txt["primary"],
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                        },
                    }}
                >
                    <AddOutlinedIcon />
                    <Text sx={{ color: "inherit" }}>{t("db_add_report")}</Text>
                </ButtonBase>
                <ButtonBase
                    onClick={(e) => {
                        stop(e);
                        handleRemoveColumn(containerKey, si, ri, ci, di);
                    }}
                    sx={{
                        flex:1,
                        borderRadius: 4, 
                        fontSize: 14, 
                        background: colors.red[100], 
                        color: colors.red[400], 
                        fontWeight:500, 
                        fontFamily:"Roboto, sans-serif",
                        transition:"250ms",
                        "&:hover": { 
                            background:colors.red[200]
                        },
                    }}
                >
                    {t("db_clear")}
                </ButtonBase>
            </Box>
        );
    }

    const FormatIcon = isComponent ? WidgetsOutlinedIcon : isPivot ? TableChartOutlinedIcon : InsertChartOutlinedIcon;
    const formatLabel = isComponent
        ? (element ? t(element.group) : "")
        : isPivot
        ? `${t(VARIANT_LABEL[c.variant] || "db_view_top")}${c.variant !== "full" && c.n ? " " + c.n : ""}`
        : t("db_format_chart");

    return (
        <Box sx={{
            flex: 1,
            mr: 3,
            position: "relative",
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            overflow: "hidden",
            userSelect: "none",
            "& .chart-controls": { opacity: 0, pointerEvents: "none", transition: "opacity 120ms ease" },
            "&:hover .chart-controls": { opacity: 1, pointerEvents: "auto" },
        }}>
            {!isComponent && (
                <Box 
                    sx={{ 
                        display: "flex", 
                        justifyContent: "flex-start",
                        flexDirection:'column',
                        // gap: 1, 
                        color: accent[400] 
                    }}
                >
                    {/* <FormatIcon fontSize="small" /> */}
                    <Text semibold sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.name || t(isPivot ? "db_format_pivot" : "db_format_chart")}
                    </Text>
                    <Text light sx={{ fontSize: 12 }}>{[c.type && t(c.type), formatLabel].filter(Boolean).join(" · ")}</Text>
                </Box>
            )}
            

            {/* Component cell: show the real widget's query-free skeleton (its fetch is skipped via `preview`). */}
            {isComponent && element && (
                <Box sx={{ flex: 1, minHeight: 120, my: 0.5, display: "flex", alignItems: "stretch", overflow: "hidden", pointerEvents: "none" }}>
                    {element.skeletonRows ? (
                        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                            {Array.from({ length: element.skeletonRows }, (_, i) => <ShRowSkeleton key={i} />)}
                        </Box>
                    ) : (
                        <ErrorBoundary FallbackComponent={() => null} onError={(e) => console.error(e)}>
                            <element.Component preview isLoading isFetching />
                        </ErrorBoundary>
                    )}
                </Box>
            )}

            {/* Report cell: a query-free type-shaped skeleton — the real chart renderer in its loading state
                (so a bar/line/area report looks like itself), or skeleton rows for a pivot. No data is fetched. */}
            {hasReport && (
                <Box sx={{ flex: 1, minHeight: 140, my: 0.5, overflow: "hidden", pointerEvents: "none" }}>
                    {isPivot ? (
                        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                            {[0, 1, 2, 3].map((i) => <ShRowSkeleton key={i} />)}
                        </Box>
                    ) : (
                        <ErrorBoundary FallbackComponent={() => null} onError={(e) => console.error(e)}>
                            <RhDashboardChart chart={c} preview />
                        </ErrorBoundary>
                    )}
                </Box>
            )}

            {/* Hover-only overlay so the actions don't compete with the chart preview at rest. */}
            <Box 
                className="chart-controls" 
                sx={{ 
                    position: "absolute", 
                    left:"10%",
                    top:"25%",
                    display: "flex", 
                    flexDirection:'column',
                    justifyContent:"center",
                    borderRadius:5,
                    width:"80%",
                    height:"50%",
                    px:4,
                    background:theme.palette.mode === "dark" ? "rgba(255,255,255,.25)" : "rgba(0,0,0,.25)",
                    gap: 1, 
                    zIndex: 3 
                }}
            >
                <ButtonBase
                    onPointerDown={stop}
                    onClick={(e) => { stop(e); onPick?.(); }}
                    sx={{ 
                        px: 2, 
                        height: 32, 
                        borderRadius: 4, 
                        fontSize: 14, 
                        background: isDark ? colors.grey[500] : colors.grey[400], 
                        color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt['light'],
                        fontWeight:500, 
                        // boxShadow: "0px 2px 8px rgba(0,0,0,0.15)" 
                    }}
                >
                    {t("db_change")}
                </ButtonBase>
                <ButtonBase
                    onPointerDown={stop}
                    onClick={(e) => {
                        stop(e);
                        handleRemoveColumn(containerKey, si, ri, ci, di);
                    }}
                    sx={{ 
                        px: 2, 
                        height: 32, 
                        borderRadius: 4, 
                        fontSize: 14, 
                        background: colors.red[400], 
                        color: theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light'], 
                        fontWeight:500, 
                        // boxShadow: "0px 2px 8px rgba(0,0,0,0.15)" 
                    }}
                >
                    {t("db_clear")}
                </ButtonBase>
            </Box>
        </Box>
    );
};
